import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  FileText, 
  Plus,
  Edit,
  Trash2,
  Loader2,
  Save,
  GripVertical,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Copy,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED_ROLES = ['system_admin', 'executive', 'operations_manager'];

// Merge fields available for SNAP Outreach templates
const MERGE_FIELDS = [
  { label: 'Participant Name', value: '[PARTICIPANT_NAME]' },
  { label: 'Current Date', value: '[CURRENT_DATE]' },
  { label: 'User Name', value: '[USER_NAME]' },
  { label: 'Case Manager', value: '[CASE_MANAGER]' },
  { label: 'Phone', value: '[PHONE]' },
  { label: 'Email', value: '[EMAIL]' },
];

// Reporting categories for SNAP Outreach reports
const REPORTING_CATEGORIES = [
  { value: '', label: 'None' },
  { value: 'new_applicant', label: 'New Applicant' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'redetermination', label: 'Redetermination' },
  { value: 'snap_approved', label: 'SNAP Approved' },
  { value: 'snap_denied', label: 'SNAP Denied' },
  { value: 'referral', label: 'Referral' },
  { value: 'closed', label: 'Closed' },
];

// State options
const STATE_OPTIONS = [
  { value: 'NC', label: 'North Carolina (NC)' },
  { value: 'TN', label: 'Tennessee (TN)' },
];

const SnapOutreachQuickNoteTemplatesPage = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    reporting_category: '',
    state: 'NC',
    is_active: true
  });

  const hasAccess = ALLOWED_ROLES.includes(user?.role);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/settings/snap-outreach-quick-note-templates?include_inactive=true`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!formData.subject.trim() || !formData.content.trim()) {
      toast.error('Subject and content are required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/settings/snap-outreach-quick-note-templates`, formData);
      toast.success('Template created successfully');
      setShowCreateDialog(false);
      setFormData({ subject: '', content: '', reporting_category: '', state: 'NC', is_active: true });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!formData.subject.trim() || !formData.content.trim()) {
      toast.error('Subject and content are required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings/snap-outreach-quick-note-templates/${selectedTemplate.id}`, formData);
      toast.success('Template updated successfully');
      setShowEditDialog(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/settings/snap-outreach-quick-note-templates/${selectedTemplate.id}`);
      toast.success('Template deleted successfully');
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await axios.put(`${API}/settings/snap-outreach-quick-note-templates/${template.id}`, {
        is_active: !template.is_active
      });
      toast.success(`Template ${template.is_active ? 'disabled' : 'enabled'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newOrder = [...templates];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    
    const orderData = newOrder.map((t, i) => ({ id: t.id, sort_order: i + 1 }));
    
    try {
      await axios.post(`${API}/settings/snap-outreach-quick-note-templates/reorder`, orderData);
      setTemplates(newOrder);
    } catch (error) {
      toast.error('Failed to reorder templates');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === templates.length - 1) return;
    const newOrder = [...templates];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    
    const orderData = newOrder.map((t, i) => ({ id: t.id, sort_order: i + 1 }));
    
    try {
      await axios.post(`${API}/settings/snap-outreach-quick-note-templates/reorder`, orderData);
      setTemplates(newOrder);
    } catch (error) {
      toast.error('Failed to reorder templates');
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      subject: template.subject,
      content: template.content,
      reporting_category: template.reporting_category || '',
      state: template.state || 'NC',
      is_active: template.is_active
    });
    setShowEditDialog(true);
  };

  const openPreviewDialog = (template) => {
    setSelectedTemplate(template);
    setShowPreviewDialog(true);
  };

  const insertMergeField = (field) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + field
    }));
  };

  const getReportingCategoryLabel = (value) => {
    const cat = REPORTING_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : 'None';
  };

  if (!hasAccess) {
    return (
      <div className="p-6">
        <Card className="card-shadow max-w-lg mx-auto">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-bold text-destructive mb-2">Access Denied</h2>
              <p className="text-muted-foreground">
                Only System Administrators, Executives, and Operations Managers can manage Quick Note templates.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="snap-outreach-quick-notes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">SNAP Outreach Quick Note Templates</h1>
          <p className="text-muted-foreground">
            Manage templates for quick case note creation in SNAP Outreach cases
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-snap-outreach-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create SNAP Outreach Quick Note Template</DialogTitle>
              <DialogDescription>
                Create a new template for SNAP Outreach quick notes. Use merge fields to auto-populate data.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Subject (Case Note Title) *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="e.g., New SNAP Application Submitted"
                  data-testid="template-subject-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Reporting Category</Label>
                  <Select
                    value={formData.reporting_category}
                    onValueChange={(value) => setFormData({ ...formData, reporting_category: value })}
                  >
                    <SelectTrigger data-testid="reporting-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORTING_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value || 'none'} value={cat.value || 'none'}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Used for SNAP Outreach reporting metrics
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(value) => setFormData({ ...formData, state: value })}
                  >
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Available Merge Fields</Label>
                <div className="flex flex-wrap gap-2">
                  {MERGE_FIELDS.map((field) => (
                    <Button
                      key={field.value}
                      variant="outline"
                      size="sm"
                      onClick={() => insertMergeField(field.value)}
                      type="button"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      {field.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Template Content *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter the template content with merge fields..."
                  className="min-h-[200px] font-mono text-sm"
                  data-testid="template-content-input"
                />
                <p className="text-xs text-muted-foreground">
                  Use merge fields like [PARTICIPANT_NAME], [CURRENT_DATE], [USER_NAME], [CASE_MANAGER], [PHONE], [EMAIL]
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active (available for selection)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTemplate} disabled={saving} data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Create Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates List */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Templates ({templates.length})
          </CardTitle>
          <CardDescription>
            Reorder templates using arrows. Active templates appear in the SNAP Outreach Quick Note dropdown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length > 0 ? (
            <div className="space-y-3">
              {templates.map((template, index) => (
                <div 
                  key={template.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    template.is_active ? 'bg-card' : 'bg-muted/50 opacity-70'
                  }`}
                  data-testid={`template-item-${template.id}`}
                >
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === templates.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab" />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium truncate">{template.subject}</h3>
                      {!template.is_active && (
                        <Badge variant="secondary" className="text-xs">Disabled</Badge>
                      )}
                      {template.state && (
                        <Badge variant="outline" className="text-xs">{template.state}</Badge>
                      )}
                      {template.reporting_category && (
                        <Badge variant="default" className="text-xs bg-blue-100 text-blue-800">
                          {getReportingCategoryLabel(template.reporting_category)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate mt-1">
                      {template.content.substring(0, 100)}...
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={template.is_active}
                      onCheckedChange={() => handleToggleActive(template)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openPreviewDialog(template)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(template)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowDeleteDialog(true);
                      }}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No templates found</p>
              <p className="text-sm">Create your first SNAP Outreach Quick Note template to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit SNAP Outreach Quick Note Template</DialogTitle>
            <DialogDescription>
              Update the template content and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject (Case Note Title) *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                data-testid="edit-template-subject"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reporting Category</Label>
                <Select
                  value={formData.reporting_category || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, reporting_category: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORTING_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value || 'none'} value={cat.value || 'none'}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>State</Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Available Merge Fields</Label>
              <div className="flex flex-wrap gap-2">
                {MERGE_FIELDS.map((field) => (
                  <Button
                    key={field.value}
                    variant="outline"
                    size="sm"
                    onClick={() => insertMergeField(field.value)}
                    type="button"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    {field.label}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Template Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="min-h-[200px] font-mono text-sm"
                data-testid="edit-template-content"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTemplate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Template
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="py-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{selectedTemplate.subject}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTemplate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              Preview how the template will appear with sample data.
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="py-4 space-y-4">
              <div>
                <Label className="text-muted-foreground">Subject</Label>
                <p className="font-medium text-lg">{selectedTemplate.subject}</p>
              </div>
              <div className="flex gap-4">
                {selectedTemplate.state && (
                  <div>
                    <Label className="text-muted-foreground">State</Label>
                    <p>{selectedTemplate.state}</p>
                  </div>
                )}
                {selectedTemplate.reporting_category && (
                  <div>
                    <Label className="text-muted-foreground">Reporting Category</Label>
                    <p>{getReportingCategoryLabel(selectedTemplate.reporting_category)}</p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground">Content (with merge fields)</Label>
                <div className="mt-2 p-4 bg-muted rounded-lg">
                  <pre className="whitespace-pre-wrap font-mono text-sm">
                    {selectedTemplate.content
                      .replace(/\[PARTICIPANT_NAME\]/g, 'John Doe')
                      .replace(/\[CURRENT_DATE\]/g, formatDate(new Date()) || '')
                      .replace(/\[USER_NAME\]/g, user?.name || 'Case Manager')
                      .replace(/\[CASE_MANAGER\]/g, user?.name || 'Jane Smith')
                      .replace(/\[PHONE\]/g, '(555) 123-4567')
                      .replace(/\[EMAIL\]/g, 'john.doe@example.com')
                    }
                  </pre>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SnapOutreachQuickNoteTemplatesPage;
